<script setup lang="ts">
import type { Component } from 'vue';
import type { IdeoToastClock } from './service';
import { resolveIdeoToastProgress } from './progress';
import type { IdeoToastAction, IdeoToastVariant } from './types';

defineOptions({
    name: 'IdeoToast'
});

const props = withDefaults(defineProps<{
    variant?: IdeoToastVariant;
    message: string;
    description?: Component | string;
    action?: IdeoToastAction;
    dismissible?: boolean;
    duration?: number;
    deadline?: number;
    clock?: IdeoToastClock;
    onPauseChange?: (paused: boolean) => void;
    onCloseToast?: () => void;
}>(), {
    variant: 'default',
    description: undefined,
    action: undefined,
    dismissible: true,
    duration: undefined,
    deadline: undefined,
    clock: undefined,
    onPauseChange: undefined,
    onCloseToast: undefined
});

const loadingIcon = ref<HTMLElement | null>(null);
const progressRing = ref<SVGCircleElement | null>(null);
const toastRoot = ref<HTMLElement | null>(null);
const entryAnimation = ref<Animation | null>(null);
const loadingAnimation = ref<Animation | null>(null);
const progressAnimation = ref<Animation | null>(null);

const hovered = ref(false);
const focused = ref(false);
const dragging = ref(false);
const dragOffset = ref(0);
let dragPointer: number | undefined;
let dragStart = 0;
const paused = computed(() => hovered.value || focused.value || dragging.value);

function onFocusOut(event: FocusEvent)
{
    focused.value = !!toastRoot.value?.contains(event.relatedTarget as Node | null);
}

function startDrag(event: PointerEvent)
{
    if (!props.dismissible || event.button !== 0 || !event.isPrimary || dragPointer !== undefined)
        return;

    if ((event.target as Element).closest('button, a, input, textarea, select, [contenteditable], [role="button"]'))
        return;

    dragPointer = event.pointerId;
    dragStart = event.clientY;
    dragging.value = true;
    toastRoot.value?.setPointerCapture(event.pointerId);
}

function moveDrag(event: PointerEvent)
{
    if (event.pointerId === dragPointer)
        dragOffset.value = Math.min(0, event.clientY - dragStart);
}

function endDrag(event: PointerEvent)
{
    if (event.pointerId !== dragPointer)
        return;

    const dismiss = props.dismissible && event.type === 'pointerup' && event.clientY - dragStart <= -60;

    dragPointer = undefined;
    dragging.value = false;

    if (!dismiss)
        dragOffset.value = 0;

    if (toastRoot.value?.hasPointerCapture(event.pointerId))
        toastRoot.value.releasePointerCapture(event.pointerId);

    if (dismiss)
        close();
}

watch(paused, value => props.onPauseChange?.(value), { flush: 'sync' });

const icons: Record<IdeoToastVariant, string> = {
    default: 'bell',
    success: 'check',
    error: 'x',
    warning: 'triangle-alert',
    info: 'info',
    loading: 'refresh-cw'
};

const tones: Record<IdeoToastVariant, string> = {
    default: '[--toast-tone:oklch(0.8_0.005_265)] dark:[--toast-tone:oklch(0.5_0.01_265)]',
    success: '[--toast-tone:oklch(0.78_0.16_150)] dark:[--toast-tone:oklch(0.55_0.15_150)]',
    error: '[--toast-tone:oklch(0.72_0.19_25)] dark:[--toast-tone:oklch(0.55_0.2_25)]',
    warning: '[--toast-tone:oklch(0.83_0.15_82)] dark:[--toast-tone:oklch(0.6_0.15_70)]',
    info: '[--toast-tone:oklch(0.75_0.13_240)] dark:[--toast-tone:oklch(0.55_0.16_250)]',
    loading: '[--toast-tone:oklch(0.8_0.005_265)] dark:[--toast-tone:oklch(0.5_0.01_265)]'
};

const classes = computed(() => [
    'ideo-toast pointer-events-auto flex min-h-[52px] w-full origin-top flex-col rounded-[8px] border border-side-border bg-side-bg pt-[11px] pe-[11px] pb-[12px] ps-[13px] text-side-fg shadow-[0_8px_24px_-8px_oklch(0_0_0/0.45),0_2px_6px_-2px_oklch(0_0_0/0.3)]',
    'dark:border-[oklch(0.86_0.004_265)] dark:bg-[oklch(0.97_0_0)] dark:text-[oklch(0.2_0.02_260)]',
    props.dismissible ? 'cursor-grab touch-none select-none active:cursor-grabbing' : '',
    dragging.value ? '' : '[transition:transform_0.2s] motion-reduce:transition-none',
    tones[props.variant]
]);

const icon = computed(() => icons[props.variant]);
const hasAutoDismiss = computed(() => resolveIdeoToastProgress(props.duration, props.deadline) !== null);
const descriptionIsText = computed(() => typeof props.description === 'string');
const actionIsText = computed(() => typeof props.action?.label === 'string');

function prefersReducedMotion()
{
    return typeof window === 'undefined' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

async function updateLoadingAnimation()
{
    loadingAnimation.value?.cancel();
    loadingAnimation.value = null;

    if (props.variant !== 'loading' || prefersReducedMotion())
        return;

    await nextTick();

    const svg = loadingIcon.value?.querySelector('svg');

    if (!svg)
        return;

    loadingAnimation.value = svg.animate([
        { rotate: '0deg' },
        { rotate: '360deg' }
    ], {
        duration: 1000,
        easing: 'linear',
        iterations: Infinity
    });
}

async function updateProgressAnimation()
{
    await nextTick();

    progressAnimation.value?.cancel();
    progressAnimation.value = null;

    const clock = props.clock;
    const progress = resolveIdeoToastProgress(props.duration, clock?.deadline ?? props.deadline,
        clock?.paused && clock.deadline !== undefined ? clock.deadline - clock.remaining : Date.now());

    if (!progress || prefersReducedMotion())
        return;

    if (!progressRing.value)
        return;

    progressRing.value.style.strokeDashoffset = String(progress.offset);

    if (progress.remaining === 0)
        return;

    progressAnimation.value = progressRing.value.animate([
        { strokeDashoffset: String(progress.offset) },
        { strokeDashoffset: '1' }
    ], {
        duration: progress.remaining,
        easing: 'linear',
        fill: 'forwards'
    });

    if (props.clock?.paused)
        progressAnimation.value.pause();
}

function animateEntry()
{
    if (!toastRoot.value || prefersReducedMotion())
        return;

    entryAnimation.value = toastRoot.value.animate([
        { opacity: 0, translate: '0 -110%', scale: '.94' },
        { opacity: 1, translate: '0 0', scale: '1' }
    ], {
        duration: 500,
        easing: 'cubic-bezier(.16, 1.15, .3, 1)',
        fill: 'both'
    });
}

function close()
{
    props.onCloseToast?.();
}

function runAction(event: MouseEvent)
{
    props.action?.onClick(event);
    close();
}

watch(() => props.variant, updateLoadingAnimation, {
    immediate: true,
    flush: 'post'
});
watch([() => props.duration, () => props.deadline, () => props.clock?.deadline, () => props.clock?.paused], updateProgressAnimation, {
    immediate: true,
    flush: 'post'
});

onMounted(animateEntry);

onBeforeUnmount(() =>
{
    entryAnimation.value?.cancel();
    loadingAnimation.value?.cancel();
    progressAnimation.value?.cancel();
});
</script>

<template>
    <div
        ref="toastRoot"
        :class="classes"
        :style="{ transform: dragOffset ? `translateY(${dragOffset}px)` : undefined }"
        @mouseenter="hovered = true"
        @mouseleave="hovered = false"
        @focusin="focused = true"
        @focusout="onFocusOut"
        @pointerdown.stop="startDrag"
        @pointermove.stop="moveDrag"
        @pointerup.stop="endDrag"
        @pointercancel.stop="endDrag"
        @lostpointercapture="endDrag"
        :data-toast-variant="props.variant"
        data-toast-entry-duration="500"
        data-toast-entry-easing="cubic-bezier(.16, 1.15, .3, 1)"
    >
        <div class="flex items-center gap-[10px]">
            <span
                ref="loadingIcon"
                class="relative flex size-[22px] shrink-0 items-center justify-center rounded-full bg-[color-mix(in_oklch,currentColor_20%,transparent)] text-[var(--toast-tone)]"
                :data-toast-loading="props.variant === 'loading' ? 'true' : undefined"
                aria-hidden="true"
            >
                <svg
                    v-if="hasAutoDismiss"
                    class="pointer-events-none absolute -inset-[1px] size-[24px] -rotate-90 overflow-visible motion-reduce:hidden"
                    viewBox="0 0 24 24"
                    fill="none"
                    data-toast-progress="true"
                    :data-toast-progress-duration="props.duration"
                    data-toast-progress-easing="linear"
                    aria-hidden="true"
                >
                    <circle
                        ref="progressRing"
                        class="stroke-current"
                        cx="12"
                        cy="12"
                        r="11.5"
                        pathLength="1"
                        stroke-width="1"
                        stroke-linecap="round"
                        stroke-dasharray="1"
                        stroke-dashoffset="0"
                        vector-effect="non-scaling-stroke"
                    />
                </svg>
                <IdeoIcon class="relative z-1" :name="icon" :size="13" :stroke-width="2.4" />
            </span>
            <span class="min-w-0 flex-1 text-ui-body/[1.35] font-[600] text-[var(--toast-tone)]">
                {{ props.message }}
            </span>
            <button
                v-if="props.dismissible"
                type="button"
                class="-my-0 flex size-[30px] shrink-0 cursor-pointer items-center justify-center rounded-[8px] border-0 bg-transparent p-0 text-side-muted [transition:color_0.12s,background_0.12s] hover:bg-side-hover hover:text-side-fg focus-visible:outline-[2px] focus-visible:outline-offset-[1px] focus-visible:outline-[color-mix(in_oklch,var(--color-side-fg)_50%,transparent)] max-lg:-my-[7px] max-lg:-me-[7px] max-lg:ms-0 max-lg:size-[44px] dark:text-[oklch(0.5_0.01_265)] dark:hover:bg-[oklch(0.92_0.004_265)] dark:hover:text-[oklch(0.2_0.02_260)]"
                aria-label="Zamknij"
                @click="close"
            >
                <IdeoIcon name="x" :size="13" :stroke-width="2.4" aria-hidden="true" />
            </button>
        </div>
        <div v-if="props.description || props.action" class="mt-[4px] flex flex-col ps-[32px]">
            <span v-if="props.description" class="text-ui-label/[1.4] text-side-muted dark:text-[oklch(0.45_0.01_265)]">
                <template v-if="descriptionIsText">{{ props.description }}</template>
                <component :is="props.description" v-else />
            </span>
            <span v-if="props.action" :class="props.description ? 'mt-[10px]' : ''">
                <IdeoAction variant="primary" @click="runAction">
                    <template v-if="actionIsText">{{ props.action.label }}</template>
                    <component :is="props.action.label" v-else />
                </IdeoAction>
            </span>
        </div>
    </div>
</template>
